import { useCallback, useContext } from 'react';
import { Box3, OrthographicCamera, PerspectiveCamera, Sphere, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { isDefined } from '../../../../utils/variables';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';

const FIT_PADDING = 1.2;

export function centerCameraOnBoundingBox(
  controls: OrbitControls,
  camera: PerspectiveCamera | OrthographicCamera,
  boundingBox: Box3,
) {
  const center = boundingBox.getCenter(new Vector3());
  const size = new Vector3();
  boundingBox.getSize(size);

  const isEmpty = size.x === 0 && size.y === 0 && size.z === 0;

  if (camera instanceof OrthographicCamera) {
    controls.target.copy(center);
    camera.position.set(center.x, center.y, 5000);
    if (!isEmpty) {
      const viewWidth = camera.right - camera.left;
      const viewHeight = camera.top - camera.bottom;
      const zoomX = viewWidth / (size.x * FIT_PADDING);
      const zoomY = viewHeight / (size.y * FIT_PADDING);
      camera.zoom = Math.min(zoomX, zoomY, 100);
      camera.zoom = Math.max(0.001, camera.zoom);
      camera.updateProjectionMatrix();
    }
  } else {
    const direction = camera.position.clone().sub(controls.target);
    if (direction.lengthSq() < 1e-10) {
      direction.set(0, 0, 1);
    } else {
      direction.normalize();
    }
    controls.target.copy(center);
    if (!isEmpty) {
      const sphere = new Sphere();
      boundingBox.getBoundingSphere(sphere);
      const vFovRad = (camera.fov * Math.PI) / 180;
      const distance = (sphere.radius / Math.tan(vFovRad / 2)) * FIT_PADDING;
      camera.position.copy(
        controls.target.clone().add(direction.multiplyScalar(Math.max(distance, 10))),
      );
    } else {
      camera.position.copy(controls.target.clone().add(direction.multiplyScalar(100)));
    }
  }

  controls.update();
}

export function useCenterCameraCallback(
  controls?: OrbitControls,
  camera?: PerspectiveCamera | OrthographicCamera,
) {
  const { boundingBox } = useContext(GeodataSpatialReferenceContext);

  const centerCamera = useCallback(() => {
    if (isDefined(camera) && isDefined(controls) && isDefined(boundingBox)) {
      centerCameraOnBoundingBox(controls, camera, boundingBox);
    }
  }, [controls, camera, boundingBox]);

  return centerCamera;
}
